import { coreVersions } from '@emma/core/core';
import { Flex, Link, Text, Tooltip } from '@lego/klik-ui';
import { StatusCircleCheck, StatusInprogress } from '@lego/klik-ui/icons';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  ImmediateStrategyWarning,
  isImmediateStrategy,
} from '@/components/common/ImmediateStrategyWarning';
import { DeviceDeploymentBadge } from '@/components/device/DeviceDeploymentBadge';
import { ConnectedStatusIcon } from '@/components/device/icons/ConnectedStatusIcon';
import { ReportedCoreVersion } from '@/components/device/ReportedCoreVersion';
import { FleetNameBadge } from '@/components/fleet/FleetBadge';
import type { DeviceResponse } from '@/hooks/device';
import { dateTimeFormat } from '@/utility/date';
import { paths } from '@/utility/global_paths';
import { CopyableText } from '../../CopyableText';
import { PlaceholderText } from '../../PlaceholderText';
import { createColumnHelper } from '../appTable';

const columnHelper = createColumnHelper<DeviceResponse>();

export const deviceTableColumns = columnHelper.columns([
  columnHelper.accessor('id', {
    cell: (info) => (
      <Text fontFamily="mono" fontSize="sm">
        {info.getValue()}
      </Text>
    ),
    header: 'ID',
    meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
    size: 96,
  }),
  columnHelper.accessor('state', {
    cell: (info) => (info.getValue() === 'PROVISIONED' ? 'Provisioned' : 'Registered'),
    enableSorting: false,
    header: 'State',
    size: 130,
    meta: {
      filterVariant: {
        options: [
          { filterValue: 'REGISTERED', name: 'Registered' },
          { filterValue: 'PROVISIONED', name: 'Provisioned' },
        ],
        type: 'singleSelect',
      },
    },
  }),
  columnHelper.accessor('name', {
    cell: (info) => <CopyableText value={info.getValue()} />,
    header: 'Name',
    meta: { filterVariant: { type: 'singleSearch' } },
  }),
  columnHelper.accessor('macAddress', {
    cell: (info) => (
      <CopyableText textProps={{ fontFamily: 'mono', fontSize: 'sm' }} value={info.getValue()} />
    ),
    header: 'Mac Address',
    meta: { filterVariant: { type: 'singleSearch' } },
  }),
  columnHelper.accessor(
    (row) => ({
      ip: row.ip,
      desiredIp: row.desiredIp,
      state: row.state,
    }),
    {
      cell: (info) => {
        const { ip, desiredIp, state } = info.getValue();
        if (state === 'REGISTERED') return <PlaceholderText>Not provisioned</PlaceholderText>;

        return (
          <Flex align="center">
            {ip != null ? (
              <CopyableText textProps={{ fontFamily: 'mono', fontSize: 'sm' }} value={ip} />
            ) : (
              <PlaceholderText>Unknown</PlaceholderText>
            )}
            {desiredIp == null ? null : ip === desiredIp ? (
              <Tooltip label="The device is running the desired IP address.">
                <StatusCircleCheck boxSize={5} color="green" mb={0.5} ml={2} />
              </Tooltip>
            ) : (
              <Tooltip
                label={`The device is not running the desired IP address. Desired: ${desiredIp}`}
              >
                <StatusInprogress boxSize={5} color="grey" mb={0.5} ml={2} />
              </Tooltip>
            )}
          </Flex>
        );
      },
      header: 'Reported IP',
      id: 'reportedIp',
      meta: { filterVariant: { type: 'singleSearch' } },
    }
  ),
  columnHelper.accessor(
    (row) => ({
      isConnected: row.isConnected,
      isConnectedUpdatedAt: row.isConnectedUpdatedAt,
      state: row.state,
    }),
    {
      cell: (info) => {
        const { isConnected, isConnectedUpdatedAt, state } = info.getValue();
        if (state === 'REGISTERED') return <PlaceholderText>Not provisioned</PlaceholderText>;

        return (
          <ConnectedStatusIcon
            isConnected={isConnected}
            isConnectedUpdatedAt={isConnectedUpdatedAt}
          />
        );
      },
      header: 'Connected',
      id: 'isConnected',
      size: 160,
      meta: {
        filterVariant: {
          options: [
            { filterValue: true, name: 'Online' },
            { filterValue: false, name: 'Offline' },
          ],
          type: 'singleSelect',
        },
      },
    }
  ),
  columnHelper.accessor('workspaceId', {
    cell: (info) => (
      <Link
        as={ReactRouterLink}
        fontFamily="mono"
        fontSize="sm"
        p={0}
        to={paths.workspaceById(info.getValue())}
      >
        {info.getValue()}
      </Link>
    ),
    header: 'Workspace ID',
    id: 'workspace',
    size: 160,
    meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  }),
  columnHelper.accessor(
    (row) => ({
      fleets: [...row.fleets].sort((a, b) => a.id - b.id),
      workspaceId: row.workspaceId,
    }),
    {
      cell: (info) => {
        const { fleets, workspaceId } = info.getValue();

        return fleets.length > 0 ? (
          <Flex flexWrap="wrap" gap={1}>
            {fleets.map((f) => (
              <FleetNameBadge
                fleetId={f.id}
                isCohosted={f.workspaceId !== workspaceId}
                key={f.id}
                name={f.name}
                workspaceId={f.workspaceId}
              />
            ))}
          </Flex>
        ) : (
          <PlaceholderText>Unassigned</PlaceholderText>
        );
      },
      enableSorting: false,
      header: 'Fleets',
      id: 'fleet',
      meta: {
        filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' },
      },
      size: 240,
    }
  ),
  columnHelper.accessor('workcenterId', {
    cell: (info) => {
      if (info.row.original.state === 'REGISTERED') {
        return <PlaceholderText>Not provisioned</PlaceholderText>;
      }

      const workcenterId = info.getValue();
      return workcenterId ? `${workcenterId}` : <PlaceholderText>Unknown</PlaceholderText>;
    },
    enableSorting: false,
    header: 'Workcenter ID',
    id: 'workcenter',
    meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  }),
  columnHelper.accessor('os', {
    cell: (info) => {
      if (info.row.original.state === 'REGISTERED') {
        return <PlaceholderText>Not provisioned</PlaceholderText>;
      }

      const osInfo = info.getValue();
      return osInfo?.name ? (
        `${osInfo.name} ${osInfo.version ?? ''}`
      ) : (
        <PlaceholderText>Unknown</PlaceholderText>
      );
    },
    enableSorting: false,
    header: 'Operating System',
    meta: {
      filterVariant: {
        options: [
          { filterValue: 'emmaOS', name: 'emmaOS' },
          { filterValue: 'Ubuntu', name: 'Ubuntu' },
        ],
        type: 'singleSelect',
      },
    },
  }),
  columnHelper.accessor((row) => row.core.reportedVersion, {
    cell: (info) => {
      const device = info.row.original;
      if (device.state === 'REGISTERED') return <PlaceholderText>Not provisioned</PlaceholderText>;

      return <ReportedCoreVersion device={device} />;
    },
    enableSorting: true,
    header: 'Core Version',
    id: 'reportedCoreVersion',
    meta: {
      filterVariant: {
        options: [...coreVersions].map((v) => ({ filterValue: v, name: v })),
        type: 'singleSelect',
      },
    },
  }),
  columnHelper.accessor('firewallName', {
    cell: (info) => {
      if (info.row.original.state === 'REGISTERED') {
        return <PlaceholderText>Not provisioned</PlaceholderText>;
      }

      const firewallName = info.getValue();
      return firewallName ? (
        <CopyableText value={firewallName} />
      ) : (
        <PlaceholderText>Unknown</PlaceholderText>
      );
    },
    enableSorting: false,
    header: 'Firewall Name',
    size: 160,
  }),
  columnHelper.accessor('firewallTemplate', {
    cell: (info) => {
      if (info.row.original.state === 'REGISTERED') {
        return <PlaceholderText>Not provisioned</PlaceholderText>;
      }

      const firewallTemplate = info.getValue();
      return firewallTemplate ? (
        <CopyableText value={firewallTemplate} />
      ) : (
        <PlaceholderText>Unknown</PlaceholderText>
      );
    },
    enableSorting: false,
    header: 'Firewall Template',
  }),
  columnHelper.accessor(
    (row) => ({
      deployments: [...row.deployments].sort((a, b) => a.deploymentId - b.deploymentId),
      fleets: row.fleets,
      state: row.state,
    }),
    {
      cell: (info) => {
        const { deployments, fleets, state } = info.getValue();
        if (state === 'REGISTERED') return <PlaceholderText>Not provisioned</PlaceholderText>;

        if (deployments.length === 0) {
          return <PlaceholderText>No active deployments</PlaceholderText>;
        }

        const workspaceByFleetId = new Map(fleets.map((f) => [f.id, f.workspaceId]));

        return (
          <Flex gap={1}>
            {deployments.map((deployment) => (
              <DeviceDeploymentBadge
                deployment={deployment}
                key={deployment.deploymentId}
                workspaceId={workspaceByFleetId.get(deployment.fleetId)}
              />
            ))}
          </Flex>
        );
      },
      enableSorting: false,
      header: 'Deployments',
      id: 'deployments',
    }
  ),
  columnHelper.accessor('deploymentStrategy', {
    cell: (info) =>
      info.row.original.state === 'REGISTERED' ? (
        <PlaceholderText>Not provisioned</PlaceholderText>
      ) : (
        <Flex align="center" gap={2}>
          {isImmediateStrategy(info.getValue()) && <ImmediateStrategyWarning />}
          <Text fontFamily="mono" fontSize="sm">
            {info.getValue() || 'Default'}
          </Text>
        </Flex>
      ),
    enableSorting: false,
    header: 'Deployment Strategy',
  }),
  columnHelper.accessor('createdAt', {
    cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    header: 'Created At',
    meta: { filterVariant: { type: 'dateRange' } },
  }),
]);
